import { PhoneIcon, CalendarIcon, ClockIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { useContent } from '../context/ContentContext';

export default function CTA() {
  const { content, settings } = useContent();
  const cta = content.cta;

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-brand-navy relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-orange rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-tan rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-10 xl:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 border border-brand-orange/30 rounded-lg px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-brand-orange rounded-full" />
              <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">
                {cta.sectionLabel}
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-white mb-4 sm:mb-6">
              {cta.heading}
            </h2>

            <p className="text-base sm:text-lg text-white/80 leading-relaxed mb-6 sm:mb-8">
              {cta.description}
            </p>

            <div className="grid grid-cols-2 gap-3">
              {cta.benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-2 sm:gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-brand-orange flex-shrink-0" />
                  <span className="text-white font-medium text-sm sm:text-base">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-10 shadow-2xl">
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-flex items-center gap-2 bg-brand-off-white rounded-full px-4 py-2 mb-4">
                <ClockIcon className="w-5 h-5 text-brand-orange" />
                <span className="text-brand-navy font-medium text-sm">{cta.sameDayLabel}</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-serif font-bold text-brand-navy">
                {cta.subheading}
              </h3>
            </div>

            <div className="space-y-4">
              <a
                href={settings.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full bg-brand-orange text-white px-6 sm:px-8 py-4 sm:py-5 rounded-xl font-semibold text-base sm:text-lg hover:bg-brand-tan transition-colors duration-200 shadow-lg group"
              >
                <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform" />
                <span>{cta.buttonLabel}</span>
              </a>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-4 text-sm text-brand-warm-gray">{cta.callText}</span>
                </div>
              </div>

              <a
                href={`tel:${settings.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center justify-center gap-3 w-full bg-brand-navy text-white px-6 sm:px-8 py-4 sm:py-5 rounded-xl font-semibold text-base sm:text-lg hover:bg-brand-navy/90 transition-colors duration-200 group"
              >
                <PhoneIcon className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform" />
                <span>{settings.phone}</span>
              </a>
            </div>

            <p className="text-center text-sm text-brand-warm-gray mt-5 sm:mt-6">
              {cta.tagline}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
